import React, { memo, useCallback, useRef } from 'react';
import { bool } from 'prop-types';
import classNames from 'classnames';
import { Document, HeartInactive } from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import { TextLink } from '@johnlewispartnership/wtr-ingredients/ingredients/TextLink';
import { Link } from 'react-router-dom';

import { yieldToMain } from 'utils/yield-to-main';
import { pushMenuClick } from 'utils/gtm';

import urls from 'constants/urls';

import SignIn from 'components/AuthenticationAction/SignIn';
import { LoadableFavouritesPage, LoadableMyOrdersPage } from 'components/App/loadableComponents';

import SeasonalMenu from '../SeasonalMenu';
import MyAccount from './MyAccount/MyAccount';

import styles from './LinksBar.scss';

const ConnectedLinksBar = memo(({ isLoggedIn = false }) => {
  const menuClickHandler = useCallback(event => {
    yieldToMain().then(() => {
      pushMenuClick(event.target.textContent);
    });
  }, []);

  const componentPreloadRef = useRef({
    favouritesPage: false,
    myOrdersPage: false,
  });

  const preloadFavourites = useCallback(() => {
    if (componentPreloadRef.current.favouritesPage) {
      return;
    }

    componentPreloadRef.current.favouritesPage = true;
    LoadableFavouritesPage.preload();
  }, []);

  const preloadMyOrders = useCallback(() => {
    if (componentPreloadRef.current.myOrdersPage) {
      return;
    }

    componentPreloadRef.current.myOrdersPage = true;
    LoadableMyOrdersPage.preload();
  }, []);

  const unauthenticatedAccountLinks = (
    <nav
      className={styles.secondaryLinks}
      data-testid="account-link-login"
      aria-label="Sign in and register"
    >
      <SignIn className={styles.links} />
    </nav>
  );

  const authenticatedAccountLinks = (
    <nav aria-label="Account links" className={styles.secondaryLinks}>
      <TextLink
        component={Link}
        id="site-header-favourites"
        to={urls.favourites}
        className={styles.links}
        data-testid="site-header-favourites"
        onClick={menuClickHandler}
        onMouseEnter={preloadFavourites}
        underline="none"
      >
        <HeartInactive className={styles.favouritesIcon} size="medium" />
        Favourites
      </TextLink>
      <>
        <TextLink
          component={Link}
          id="site-header-my-orders"
          to={urls.myOrdersPage}
          className={styles.links}
          data-testid="site-header-my-orders"
          onClick={menuClickHandler}
          onMouseEnter={preloadMyOrders}
          underline="none"
        >
          <Document className={styles.ordersIcon} />
          My orders
        </TextLink>
        <MyAccount />
      </>
    </nav>
  );

  const accountLinks = !isLoggedIn ? unauthenticatedAccountLinks : authenticatedAccountLinks;

  return (
    <div className={styles.siteHeaderLinks}>
      <nav className={classNames(styles.primaryLinks)} aria-label="Main navigation">
        <SeasonalMenu />
        <TextLink
          href={urls.offers}
          className={styles.offers}
          id="site-header-offers"
          data-testid="site-header-offers"
          onClick={menuClickHandler}
          underline="none"
        >
          Offers
        </TextLink>
        <TextLink
          href={urls.entertainingHome}
          className={styles.links}
          data-testid="site-header-entertaining"
          id="site-header-entertaining"
          onClick={menuClickHandler}
          underline="none"
        >
          Entertaining
        </TextLink>
        <TextLink
          href={urls.new}
          id="site-header-new"
          data-testid="site-header-new"
          className={styles.links}
          onClick={menuClickHandler}
          underline="none"
        >
          New
        </TextLink>
        <TextLink
          component={Link}
          id="site-header-recipes"
          className={styles.links}
          to="/ecom/recipes"
          onClick={menuClickHandler}
          underline="none"
        >
          Recipes
        </TextLink>
        <TextLink
          id="site-header-inspiration"
          className={styles.links}
          href={urls.inspiration}
          onClick={menuClickHandler}
          underline="none"
        >
          Inspiration
        </TextLink>
      </nav>
      {accountLinks}
    </div>
  );
});

ConnectedLinksBar.displayName = 'Links';

ConnectedLinksBar.propTypes = {
  isLoggedIn: bool,
};

export default ConnectedLinksBar;
